import { buildEmailDocumentBlockOrderIndex } from "../domain/document-blocks";
import { EMAIL_BUILDER_UNSUBSCRIBE_MERGE_TAG_KEY } from "../domain/merge-tags";
import { lintExportDocument } from "../domain/export-lint";
import {
  classifyEmailDocumentLinkUrl,
  classifyEmailDocumentSocialLinkUrl,
  normalizeEmailBuilderSocialLinkUrlFieldValue,
  normalizeRestrictedRichTextLinkHref,
  type TEmailDocumentLinkUrlClassification,
} from "../schema/emailDocumentUrlValidation";
import type { Block, DocumentSection, EmailDocument, Row } from "../domain/types";

const PUBLISH_LINK_VISIBLE_TEXT_MAX_LENGTH = 120;
const UNSUBSCRIBE_LINK_TEXT_PATTERN = /\bunsubscribe\b/i;
const UNSUBSCRIBE_MERGE_TAG_PATTERN = new RegExp(
  `\\{\\{\\s*${EMAIL_BUILDER_UNSUBSCRIBE_MERGE_TAG_KEY.replace(/\./g, "\\.")}\\s*\\}\\}`,
  "i",
);
const ANCHOR_PATTERN = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
const HREF_PATTERN = /\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i;

export type TEmailBuilderPublishLinkIssueKind = "button" | "text" | "image" | "social";

export type TEmailBuilderPublishLinkIssueReason = "missing" | "invalid";

export interface IEmailBuilderPublishLinkIssue {
  kind: TEmailBuilderPublishLinkIssueKind;
  visibleText: string;
  rawUrl: string;
  reason: TEmailBuilderPublishLinkIssueReason;
  blockId: string;
}

export type TEmailBuilderFinalizeIssueKind = TEmailBuilderPublishLinkIssueKind | "unsubscribe" | "export";

export type TEmailBuilderFinalizeIssueSeverity = "error" | "warning" | "info";

export interface IEmailBuilderFinalizeIssue {
  kind: TEmailBuilderFinalizeIssueKind;
  label: string;
  detail: string;
  blockId: string | null;
  severity: TEmailBuilderFinalizeIssueSeverity;
}

export interface IEmailBuilderFinalizeReview {
  issues: IEmailBuilderFinalizeIssue[];
}

export function normalizeEmailBuilderLinkUrlFieldValue(rawUrl: string): string {
  return normalizeRestrictedRichTextLinkHref(rawUrl.trim());
}

export function isEmailBuilderLinkUrlFieldValueValid(rawUrl: string): boolean {
  const trimmed = rawUrl.trim();
  if (trimmed === "") {
    return true;
  }

  return classifyEmailDocumentLinkUrl(normalizeEmailBuilderLinkUrlFieldValue(trimmed)) === "ok";
}

function isUnsubscribeLinkVisibleText(text: string): boolean {
  return UNSUBSCRIBE_LINK_TEXT_PATTERN.test(text.replace(/\s+/g, " ").trim());
}

function isUnsubscribeMergeTagHref(href: string): boolean {
  return UNSUBSCRIBE_MERGE_TAG_PATTERN.test(href.trim());
}

function isValidUnsubscribeLink(visibleText: string, href: string): boolean {
  return isUnsubscribeLinkVisibleText(visibleText) && isUnsubscribeMergeTagHref(href);
}

function truncatePublishLinkVisibleText(text: string): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= PUBLISH_LINK_VISIBLE_TEXT_MAX_LENGTH) {
    return normalized;
  }

  return `${normalized.slice(0, PUBLISH_LINK_VISIBLE_TEXT_MAX_LENGTH - 1)}…`;
}

function stripHtmlToVisibleText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function getBlockLabelVisibleText(raw: string, fallback: string): string {
  const visible = stripHtmlToVisibleText(raw);
  return visible.length > 0 ? visible : fallback;
}

function parseAnchorHref(attrs: string): string {
  const hrefMatch = attrs.match(HREF_PATTERN);
  return hrefMatch?.[2] ?? hrefMatch?.[3] ?? hrefMatch?.[4] ?? "";
}

function pushLinkIssue(
  issues: IEmailBuilderPublishLinkIssue[],
  issue: IEmailBuilderPublishLinkIssue,
): void {
  issues.push({
    ...issue,
    visibleText: truncatePublishLinkVisibleText(issue.visibleText),
  });
}

function classifyNormalizedLinkUrl(rawUrl: string): TEmailDocumentLinkUrlClassification {
  return classifyEmailDocumentLinkUrl(normalizeEmailBuilderLinkUrlFieldValue(rawUrl));
}

function classifyNormalizedSocialLinkUrl(rawUrl: string): TEmailDocumentLinkUrlClassification {
  return classifyEmailDocumentSocialLinkUrl(normalizeEmailBuilderSocialLinkUrlFieldValue(rawUrl));
}

function scanTextBlockLinkIssues(
  blockId: string,
  content: string,
  issues: IEmailBuilderPublishLinkIssue[],
): void {
  const anchorPattern = new RegExp(ANCHOR_PATTERN.source, ANCHOR_PATTERN.flags);
  let match: RegExpExecArray | null = anchorPattern.exec(content);

  while (match !== null) {
    const rawHref = parseAnchorHref(match[1] ?? "");
    const visible = stripHtmlToVisibleText(match[2] ?? "");

    if (!isValidUnsubscribeLink(visible, rawHref) && !isUnsubscribeLinkVisibleText(visible)) {
      const classification = classifyNormalizedLinkUrl(rawHref);
      if (classification !== "ok") {
        pushLinkIssue(issues, {
          kind: "text",
          visibleText: visible.length > 0 ? visible : "Link",
          rawUrl: rawHref,
          reason: classification,
          blockId,
        });
      }
    }

    match = anchorPattern.exec(content);
  }
}

function scanBlockLinkIssues(block: Block, issues: IEmailBuilderPublishLinkIssue[]): void {
  if (block.type === "button") {
    const buttonText = getBlockLabelVisibleText(block.text, "Button");
    if (isValidUnsubscribeLink(buttonText, block.href) || isUnsubscribeLinkVisibleText(buttonText)) {
      return;
    }

    const classification = classifyNormalizedLinkUrl(block.href);
    if (classification !== "ok") {
      pushLinkIssue(issues, {
        kind: "button",
        visibleText: buttonText,
        rawUrl: block.href,
        reason: classification,
        blockId: block.id,
      });
    }
    return;
  }

  if (block.type === "text") {
    scanTextBlockLinkIssues(block.id, block.html, issues);
    return;
  }

  if (block.type === "image") {
    const href = block.href.trim();
    if (href === "") {
      return;
    }

    const classification = classifyNormalizedLinkUrl(href);
    if (classification !== "ok") {
      const alt = block.alt.trim();
      pushLinkIssue(issues, {
        kind: "image",
        visibleText: alt.length > 0 ? alt : "Image",
        rawUrl: href,
        reason: classification,
        blockId: block.id,
      });
    }
    return;
  }

  if (block.type === "buttonRow") {
    for (const slot of block.buttons) {
      const buttonText = getBlockLabelVisibleText(slot.label, "Button");
      if (isValidUnsubscribeLink(buttonText, slot.href) || isUnsubscribeLinkVisibleText(buttonText)) {
        continue;
      }

      const classification = classifyNormalizedLinkUrl(slot.href);
      if (classification !== "ok") {
        pushLinkIssue(issues, {
          kind: "button",
          visibleText: buttonText,
          rawUrl: slot.href,
          reason: classification,
          blockId: block.id,
        });
      }
    }
    return;
  }

  if (block.type === "social") {
    for (const link of block.links) {
      const url = link.url.trim();
      if (url === "") {
        continue;
      }

      const classification = classifyNormalizedSocialLinkUrl(url);
      if (classification !== "ok") {
        pushLinkIssue(issues, {
          kind: "social",
          visibleText: link.title?.trim() || link.network,
          rawUrl: url,
          reason: classification,
          blockId: block.id,
        });
      }
    }
  }
}

function scanTextBlockUnsubscribeLink(blockId: string, content: string): "valid" | "invalid" | null {
  const anchorPattern = new RegExp(ANCHOR_PATTERN.source, ANCHOR_PATTERN.flags);
  let match: RegExpExecArray | null = anchorPattern.exec(content);
  let sawInvalid = false;

  while (match !== null) {
    const rawHref = parseAnchorHref(match[1] ?? "");
    const visible = stripHtmlToVisibleText(match[2] ?? "");

    if (isValidUnsubscribeLink(visible, rawHref)) {
      return "valid";
    }

    if (isUnsubscribeLinkVisibleText(visible)) {
      sawInvalid = true;
    }

    match = anchorPattern.exec(content);
  }

  return sawInvalid ? "invalid" : null;
}

function scanBlockUnsubscribeLink(block: Block): "valid" | "invalid" | null {
  if (block.type === "button") {
    const buttonText = getBlockLabelVisibleText(block.text, "Button");
    if (isValidUnsubscribeLink(buttonText, block.href)) {
      return "valid";
    }

    return isUnsubscribeLinkVisibleText(buttonText) ? "invalid" : null;
  }

  if (block.type === "text") {
    return scanTextBlockUnsubscribeLink(block.id, block.html);
  }

  if (block.type === "buttonRow") {
    let sawInvalid = false;

    for (const slot of block.buttons) {
      const buttonText = getBlockLabelVisibleText(slot.label, "Button");
      if (isValidUnsubscribeLink(buttonText, slot.href)) {
        return "valid";
      }

      if (isUnsubscribeLinkVisibleText(buttonText)) {
        sawInvalid = true;
      }
    }

    return sawInvalid ? "invalid" : null;
  }

  return null;
}

function visitRowBlocks(row: Row, issues: IEmailBuilderPublishLinkIssue[]): void {
  for (const column of row.columns) {
    for (const block of column.blocks) {
      scanBlockLinkIssues(block, issues);
    }
  }
}

function visitSection(section: DocumentSection, issues: IEmailBuilderPublishLinkIssue[]): void {
  if ("rows" in section) {
    for (const row of section.rows) {
      visitRowBlocks(row, issues);
    }
    return;
  }

  visitRowBlocks(section, issues);
}

function scanEmailBuilderUnsubscribeIssues(document: EmailDocument): IEmailBuilderFinalizeIssue[] {
  let invalidBlockId: string | null = null;

  for (const section of document.sections) {
    const rows = "rows" in section ? section.rows : [section];
    for (const row of rows) {
      for (const column of row.columns) {
        for (const block of column.blocks) {
          const status = scanBlockUnsubscribeLink(block);
          if (status === "valid") {
            return [];
          }

          if (status === "invalid") {
            invalidBlockId = block.id;
          }
        }
      }
    }
  }

  if (invalidBlockId !== null) {
    return [
      {
        kind: "unsubscribe",
        label: "Unsubscribe",
        detail: "Use the Unsubscribe URL merge tag in the link URL",
        blockId: invalidBlockId,
        severity: "error",
      },
    ];
  }

  return [
    {
      kind: "unsubscribe",
      label: "Unsubscribe",
      detail: "Add an unsubscribe link using the Unsubscribe URL merge tag",
      blockId: null,
      severity: "error",
    },
  ];
}

function exportLintIssueLabel(message: string): string {
  if (message.includes("contrast")) {
    return "Color contrast";
  }
  if (message.includes("Heading 1")) {
    return "Heading structure";
  }
  if (message.includes("alt text")) {
    return "Image alt text";
  }

  if (message.includes("overflow its column")) {
    return "Column layout";
  }

  return "Export check";
}

function mapExportLintIssues(document: EmailDocument): IEmailBuilderFinalizeIssue[] {
  return lintExportDocument(document).map(issue => ({
    kind: "export" as const,
    label: exportLintIssueLabel(issue.message),
    detail: issue.message,
    blockId: issue.blockId ?? null,
    severity: issue.severity,
  }));
}

const FINALIZE_ISSUE_GLOBAL_ORDER = Number.MAX_SAFE_INTEGER;

const FINALIZE_ISSUE_SEVERITY_ORDER: Record<TEmailBuilderFinalizeIssueSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

function sortFinalizeIssuesByDocumentOrder(
  issues: IEmailBuilderFinalizeIssue[],
  document: EmailDocument,
): IEmailBuilderFinalizeIssue[] {
  const blockOrder = buildEmailDocumentBlockOrderIndex(document);

  return [...issues].sort((a, b) => {
    const aOrder =
      a.blockId === null
        ? FINALIZE_ISSUE_GLOBAL_ORDER
        : blockOrder.get(a.blockId) ?? FINALIZE_ISSUE_GLOBAL_ORDER;
    const bOrder =
      b.blockId === null
        ? FINALIZE_ISSUE_GLOBAL_ORDER
        : blockOrder.get(b.blockId) ?? FINALIZE_ISSUE_GLOBAL_ORDER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    const severityDiff =
      FINALIZE_ISSUE_SEVERITY_ORDER[a.severity] - FINALIZE_ISSUE_SEVERITY_ORDER[b.severity];
    if (severityDiff !== 0) {
      return severityDiff;
    }

    return a.label.localeCompare(b.label) || a.detail.localeCompare(b.detail);
  });
}

export function scanEmailBuilderPublishLinkIssues(
  document: EmailDocument,
): IEmailBuilderPublishLinkIssue[] {
  const issues: IEmailBuilderPublishLinkIssue[] = [];

  for (const section of document.sections) {
    visitSection(section, issues);
  }

  return issues;
}

export function scanEmailBuilderFinalizeReview(document: EmailDocument): IEmailBuilderFinalizeReview {
  const unsubscribeIssues = scanEmailBuilderUnsubscribeIssues(document);
  const linkIssues = scanEmailBuilderPublishLinkIssues(document).map(issue => ({
    kind: issue.kind,
    label: issue.visibleText,
    detail: issue.reason === "missing" ? "Missing link" : "Invalid link",
    blockId: issue.blockId,
    severity: "warning" as const,
  }));

  return {
    issues: sortFinalizeIssuesByDocumentOrder(
      [...unsubscribeIssues, ...linkIssues, ...mapExportLintIssues(document)],
      document,
    ),
  };
}
